import Cookies from 'js-cookie';

// Reader SSE compartilhado (Vision + Playground): `fetch` + ReadableStream, com
// auth por header (lida do cookie `cw_d_session_info` — o EventSource nativo não
// manda header), parser de `text/event-stream` e handshake `: ready` (resolve só
// quando o servidor já assinou o subject, pra não perder os primeiros eventos do
// turn). Retorna `unsubscribe` (abort). O servidor encerra o stream no evento
// terminal (`done`/`is_final`); aqui só lemos até o fim ou até o abort.

const AUTH_HEADER_KEYS = [
  'access-token',
  'token-type',
  'client',
  'expiry',
  'uid',
];

const authHeaders = () => {
  const raw = Cookies.get('cw_d_session_info');
  if (!raw) return {};
  const all = JSON.parse(raw);
  return AUTH_HEADER_KEYS.reduce((acc, key) => {
    if (all[key] != null) acc[key] = all[key];
    return acc;
  }, {});
};

// Parser incremental: separa eventos por \n\n, extrai linhas `data:` e ignora
// comments (`:` — ready/keepalive). Devolve o resto não terminado.
const drainEvents = (buffer, onEvent) => {
  let rest = buffer;
  let sep = rest.indexOf('\n\n');
  while (sep !== -1) {
    const raw = rest.slice(0, sep);
    rest = rest.slice(sep + 2);
    const data = raw
      .split('\n')
      .filter(line => line.startsWith('data:'))
      .map(line => line.slice(5).trim())
      .join('\n');
    if (data) {
      try {
        onEvent(JSON.parse(data));
      } catch {
        // evento malformado — ignora (não derruba o stream)
      }
    }
    sep = rest.indexOf('\n\n');
  }
  return rest;
};

// createSseStream(url, onEvent) → Promise<unsubscribe>. Resolve só após o 1º byte
// (handshake `: ready`) — garante que o servidor já assinou o subject. FALHA FECHADA:
// se o stream fechar/errar ANTES do 1º byte, rejeita (o caller não posta num stream
// morto). Erro DEPOIS do ready vira um evento terminal `{type:'error'}` no onEvent
// (não engole) — a sessão para de esperar em vez de pendurar.
export const createSseStream = async (url, onEvent) => {
  const controller = new AbortController();
  const unsubscribe = () => controller.abort();

  const response = await fetch(url, {
    method: 'GET',
    headers: { Accept: 'text/event-stream', ...authHeaders() },
    signal: controller.signal,
    credentials: 'same-origin',
  });

  if (!response.ok || !response.body) {
    throw new Error(`sse stream failed: ${response.status}`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();

  let markReady;
  let failReady;
  const ready = new Promise((resolve, reject) => {
    markReady = resolve;
    failReady = reject;
  });
  let gotReady = false;

  const pump = async () => {
    let buffer = '';
    try {
      for (;;) {
        // eslint-disable-next-line no-await-in-loop
        const { value, done } = await reader.read();
        if (done) break;
        gotReady = true;
        markReady();
        buffer += decoder.decode(value, { stream: true });
        buffer = drainEvents(buffer, onEvent);
      }
      // EOF: fechou antes do handshake = stream morto → falha fechada.
      if (!gotReady) failReady(new Error('sse stream closed before ready'));
    } catch (error) {
      if (error.name === 'AbortError') return; // unsubscribe() — encerramento normal
      if (gotReady) {
        // erro pós-ready: superfície como evento terminal (não pendura a sessão).
        onEvent({ type: 'error', error: String(error?.message || error) });
      } else {
        failReady(error);
      }
    }
  };

  const pumping = pump();
  pumping.catch(() => {}); // tratado via failReady / onEvent({type:'error'})

  await ready; // lança se o stream morreu antes do handshake
  return unsubscribe;
};
